// Browser-only joiner. Connects to a host via PeerJS, then runs the
// same NTP-min clock-sync + anchor-chain Scheduler we use for the
// LAN/Rust path — only the transport changes (WebRTC DataChannel
// instead of WebSocket). That's what keeps every device sample-
// aligned within ~10 ms of each other.
//
// Why not WebRTC MediaConnection? It hides per-frame timestamps and
// gives each receiver its own opaque jitter buffer, so peers can't
// agree on a shared playout clock. Fine for a phone call; useless for
// "one speaker."
//
// iOS Safari requires a user gesture before any audio plays. We
// gate the whole pipeline behind a single "Tap to play" button: that
// click both resumes the AudioContext and constructs the P2pSession.

import { useEffect, useMemo, useRef, useState } from 'react'
import { PeerHub, displayCode, type PeerEvent } from './PeerHub'
import { P2pSession } from './p2pSession'

type Status =
  | 'connecting'
  | 'tap-to-play'
  | 'waiting-for-audio'
  | 'playing'
  | 'reconnecting'
  | 'ended'
  | 'error'

export function BrowserJoin({
  hostId,
  onExit,
}: {
  hostId: string
  onExit: () => void
}) {
  const [status, setStatus] = useState<Status>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [volume, setVolume] = useState(0.8)
  const [latencyOffset, setLatencyOffset] = useState(0)
  const [rtt, setRtt] = useState<number | null>(null)
  const [offset, setOffset] = useState<number | null>(null)
  const [deviceLatency, setDeviceLatency] = useState<number | null>(null)

  const hubRef = useRef<PeerHub | null>(null)
  const sessionRef = useRef<P2pSession | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)

  const niceCode = useMemo(() => displayCode(hostId), [hostId])

  useEffect(() => {
    const hub = new PeerHub()
    hubRef.current = hub
    const onPeer = (e: Event) => {
      const ev = (e as CustomEvent<PeerEvent>).detail
      switch (ev.type) {
        case 'connected':
          setStatus((s) => (s === 'connecting' ? 'tap-to-play' : s))
          break
        case 'host-disconnected':
          setStatus('ended')
          break
        case 'connection-lost':
          setStatus('reconnecting')
          break
        case 'error':
          setError(ev.message)
          setStatus('error')
          break
      }
    }
    hub.addEventListener('peer', onPeer)

    hub.startAsJoiner(hostId).catch((e) => {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('error')
    })

    return () => {
      hub.removeEventListener('peer', onPeer)
      sessionRef.current?.destroy()
      sessionRef.current = null
      void ctxRef.current?.close()
      ctxRef.current = null
      hub.destroy()
      hubRef.current = null
    }
  }, [hostId])

  // Auto-reconnection loop
  useEffect(() => {
    if (status !== 'reconnecting') return

    let active = true
    let retryTimeout: ReturnType<typeof setTimeout>
    const maxRetryTime = 30_000 // 30 seconds
    const startTime = Date.now()

    const attemptReconnect = async () => {
      if (!active) return
      try {
        const hub = hubRef.current
        if (!hub) return
        await hub.reconnectAsJoiner(hostId)
        if (active) {
          setStatus(sessionRef.current ? 'playing' : 'waiting-for-audio')
        }
      } catch (e) {
        if (!active) return
        if (Date.now() - startTime > maxRetryTime) {
          setStatus('ended')
          setError('Connection lost. Host is unreachable.')
        } else {
          retryTimeout = setTimeout(attemptReconnect, 2500)
        }
      }
    }

    void attemptReconnect()

    return () => {
      active = false
      clearTimeout(retryTimeout)
    }
  }, [status, hostId])

  useEffect(() => {
    sessionRef.current?.setVolume(volume)
  }, [volume])

  useEffect(() => {
    sessionRef.current?.scheduler.setLatencyOffsetUs(latencyOffset * 1000)
  }, [latencyOffset])

  const tapToPlay = async () => {
    const hub = hubRef.current
    if (!hub) return
    if (sessionRef.current) return
    try {
      if ('audioSession' in navigator) {
        try {
          (navigator as any).audioSession.type = 'playback'
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[Multispeaker joiner] Failed to set audioSession.type to playback:', e)
        }
      }
      const ctx = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' })
      await ctx.resume()
      ctxRef.current = ctx
      const session = new P2pSession({
        ctx,
        hub,
        onPlaying: () => setStatus('playing'),
        onDiagnostics: (rttMs, offsetMs, outputLatencyMs) => {
          setRtt(rttMs)
          setOffset(offsetMs)
          setDeviceLatency(outputLatencyMs)
        },
      })
      session.setVolume(volume)
      session.scheduler.setLatencyOffsetUs(latencyOffset * 1000)
      sessionRef.current = session
      setStatus('waiting-for-audio')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('error')
    }
  }

  let statusText = ''
  switch (status) {
    case 'connecting':
      statusText = 'Connecting…'
      break
    case 'tap-to-play':
      statusText = 'Connected — tap to start playback'
      break
    case 'waiting-for-audio':
      statusText = 'Waiting for the host to share audio…'
      break
    case 'playing':
      statusText = 'Listening'
      break
    case 'reconnecting':
      statusText = 'Connection lost — trying to reconnect…'
      break
    case 'ended':
      statusText = 'Host ended the session'
      break
    case 'error':
      statusText = `Error: ${error ?? 'unknown'}`
      break
  }

  const showTapButton = status === 'tap-to-play'

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-between px-6 py-10 no-select">
      <header className="text-center">
        <h1 className="text-xl font-semibold tracking-tight">Multispeaker</h1>
        <p className="mt-1 text-xs text-zinc-500">Joining {niceCode}</p>
      </header>

      <main className="flex flex-col items-center justify-center text-center">
        {showTapButton ? (
          <button
            type="button"
            onClick={() => void tapToPlay()}
            className="rounded-full bg-blue-500 px-10 py-4 text-lg font-medium text-white shadow-lg active:scale-95 transition"
          >
            Tap to play
          </button>
        ) : (
          <p
            className={
              status === 'error'
                ? 'text-sm text-red-400'
                : status === 'ended'
                ? 'text-sm text-zinc-400'
                : status === 'reconnecting'
                ? 'text-sm text-amber-400 animate-pulse font-medium'
                : 'text-sm text-zinc-300'
            }
          >
            {statusText}
          </p>
        )}

        {(status === 'playing' || status === 'waiting-for-audio' || status === 'reconnecting') && (
          <div className="mt-8 w-full max-w-xs space-y-6">
            <div>
              <label className="mb-2 block text-sm text-zinc-400">Volume</label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
              <div className="mt-1 text-right text-xs text-zinc-500 font-mono">
                {Math.round(volume * 100)}%
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/20 p-4 text-left space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-400">Sync Adjust</span>
                <button
                  type="button"
                  onClick={() => setLatencyOffset(0)}
                  disabled={latencyOffset === 0}
                  className="text-[10px] text-zinc-500 hover:text-zinc-300 disabled:opacity-50 underline"
                >
                  reset
                </button>
              </div>
              <div className="space-y-1">
                <input
                  type="range"
                  min={-150}
                  max={150}
                  step={5}
                  value={latencyOffset}
                  onChange={(e) => setLatencyOffset(Number(e.target.value))}
                  className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-zinc-500 font-mono">
                  <span>-150ms (earlier)</span>
                  <span className={latencyOffset > 0 ? "text-emerald-400" : latencyOffset < 0 ? "text-amber-400" : ""}>
                    {latencyOffset > 0 ? `+${latencyOffset}` : latencyOffset}ms
                  </span>
                  <span>+150ms (later)</span>
                </div>
              </div>

              <details className="text-left border-t border-zinc-900 pt-3">
                <summary className="cursor-pointer text-[10px] text-zinc-400 select-none hover:text-zinc-200">
                  Diagnostics & Latency Info
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] font-mono text-zinc-500">
                  <div>Ping (RTT):</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {rtt !== null ? `${rtt.toFixed(1)} ms` : 'analyzing…'}
                  </div>
                  <div>Clock Offset:</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {offset !== null ? `${offset > 0 ? '+' : ''}${offset.toFixed(1)} ms` : 'analyzing…'}
                  </div>
                  <div>Device Latency:</div>
                  <div className="text-right text-zinc-300 font-semibold">
                    {deviceLatency !== null ? (
                      <span>
                        {deviceLatency} ms
                        <span className={`ml-1 text-[9px] font-medium ${deviceLatency > 40 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {deviceLatency > 40 ? 'Wireless/BT' : 'Speaker/Cable'}
                        </span>
                      </span>
                    ) : 'analyzing…'}
                  </div>
                  <div>Sync Status:</div>
                  <div className="text-right">
                    {rtt === null ? (
                      <span className="text-zinc-500">analyzing…</span>
                    ) : rtt < 40 ? (
                      <span className="text-emerald-400">● Good Sync</span>
                    ) : rtt < 100 ? (
                      <span className="text-amber-400">● High Jitter</span>
                    ) : (
                      <span className="text-red-400 font-semibold">● Congested / Hotspot Lag</span>
                    )}
                  </div>
                </div>
                {rtt !== null && rtt >= 50 && (
                  <p className="mt-2 text-[9px] text-zinc-500 leading-normal">
                    ⚠️ Network delay is high. Use the slider above to align with other devices. If host playback is enabled, disabling it on the host can also improve sync.
                  </p>
                )}
              </details>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center">
        <button
          type="button"
          onClick={onExit}
          className="text-xs text-zinc-500 underline-offset-2 hover:underline"
        >
          leave session
        </button>
      </footer>
    </div>
  )
}
